import {
  InstantDownloadHeaderNode,
  InstantDownloadMenuComponent,
  useCommonChartViewData,
  useTimeseriesExport,
  VisaTimePeriodFilter,
} from '@va/dashboard/components';
import { ReportBlockExportSummary, useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { ReportBlockComparisonTooltip } from '@va/dashboard/ui/components';
import { useDataView } from '@va/dashboard/util-hooks';
import { CompareIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { AddFilterButton, AppliedFilters, useFiltersContext } from '@va/shared/feature-filters';
import { DataViewOption, DataViewToggle, ReportBlockNodes } from '@va/types/report-block';
import { TimePeriod } from '@va/types/time';
import { BaseTimeseriesResponse } from '@va/types/time-series';
import { ExpandableCard, ExpandButton, useExpandableCardContext } from '@va/ui/cards';
import {
  DateComparisonPeriod,
  ReportBlockBody,
  ReportBlockHeader,
  ReportBlockNodeIds,
  ReportBlockNumericHeading,
  ReportBlockProvider,
  ReportBlockSubHeader,
  useReportBlockCtx,
} from '@va/ui/components/report-block';
import { ReportBlockNodeButton } from '@va/ui/components/report-block/nodes/ReportBlockNodeButton';
import { ReportBlockNodesTooltip } from '@va/ui/components/report-block/nodes/ReportBlockNodesTooltip';
import { ReportBlockHeaderProvider } from '@va/ui/components/report-block/report-block-header-ctx';
import { fontWeights, LineChart, Paragraph } from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { SWRResponse } from 'swr';

type VisitorStatsReportProps = {
  id: string;
  gridPosition: number;
  canExpand?: boolean;
  fetchCurrentHook: (unit?: TimePeriod) => SWRResponse<BaseTimeseriesResponse<any>, Error>;
  fetchPreviousHook: (
    activeComparisonOption: DateComparisonPeriod,
    unit?: TimePeriod,
  ) => SWRResponse<BaseTimeseriesResponse<any>, Error>;
  title: string;
  titleTooltip: string;
  valueMapper?: (total: number) => any;
  /**
   * Optional mapper used only for the numeric heading (falls back to `valueMapper`)
   */
  headingValueMapper?: (total: number) => any;
  reversePercentageColors?: boolean;
  yAxisTickCallback?: (tickValue: string | number) => string;
  showTimePeriodFilter?: boolean;
  /**
   * When set, the report block gets the instant export (CSV, XLSX, PDF, PNG) using this file name
   */
  exportFileName?: string;
};

export const VisitorStatsReport = ({ id, gridPosition, ...rest }: VisitorStatsReportProps) => {
  return (
    <ExpandableCard id={id} gridPosition={gridPosition}>
      <VisitorStatsReportContent id={id} {...rest} />
    </ExpandableCard>
  );
};

/**
 * Builds the export hook for the download nodes. The hook runs inside the nodes, so it can read the RB context
 * (comparison option, previous period toggle), and uses the same time grouping the chart shows.
 */
const buildUseExportData = (
  fileName: string,
  fetchCurrentHook: VisitorStatsReportProps['fetchCurrentHook'],
  fetchPreviousHook: VisitorStatsReportProps['fetchPreviousHook'],
  unit?: TimePeriod,
) =>
  function useVisitorStatsExportData() {
    const { activeComparisonOption } = useReportBlockCtx();
    return useTimeseriesExport({
      fileName,
      useCurrentData: () => fetchCurrentHook(unit),
      usePreviousData: () => fetchPreviousHook(activeComparisonOption, unit),
    });
  };

const VisitorStatsReportContent = ({
  id,
  canExpand = false,
  fetchCurrentHook,
  fetchPreviousHook,
  title,
  titleTooltip,
  valueMapper,
  headingValueMapper,
  reversePercentageColors = false,
  yAxisTickCallback,
  showTimePeriodFilter,
  exportFileName,
}: Omit<VisitorStatsReportProps, 'gridPosition'>) => {
  const translate = useTranslate();
  const buildReportBlockNode = useReportBlockNodeBuilder();
  const { expanded } = useExpandableCardContext();
  const { appliedFilters } = useFiltersContext();
  const reportBodyRef = useRef<HTMLDivElement | null>(null);

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.day);
  const effectiveUnit = showTimePeriodFilter ? timePeriod : undefined;

  // Only built when the export is enabled, so cards without it keep stable nodes across time period changes
  const useExportData = useMemo(
    () =>
      exportFileName
        ? buildUseExportData(exportFileName, fetchCurrentHook, fetchPreviousHook, effectiveUnit)
        : undefined,
    [exportFileName, fetchCurrentHook, fetchPreviousHook, effectiveUnit],
  );

  const showAppliedFilters = !canExpand || expanded;
  const hasAppliedFilters = appliedFilters.length > 0;

  const nodes = useMemo<ReportBlockNodes>(
    () => ({
      compareTo: buildReportBlockNode(
        ReportBlockNodeIds.COMPARE_TO,
        canExpand && !expanded ? { headerComponent: <VisitorStatsComparisonToggle /> } : undefined,
      ),
      filterButton: showAppliedFilters
        ? buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON)
        : hasAppliedFilters
          ? buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON, {
              headerComponent: (
                <AddFilterButton disabled disabledTooltip={translate('card.kpiReportBlock.filtersCollapsedTooltip')} />
              ),
            })
          : undefined,
      appliedFilters: showAppliedFilters ? <AppliedFilters /> : undefined,
      ...(exportFileName &&
        useExportData && {
          exportSummary: <ReportBlockExportSummary />,
          downloadButton: buildReportBlockNode(ReportBlockNodeIds.DOWNLOAD_BUTTON, {
            label: translate('all.exportType.instant'),
            headerComponent: (
              <InstantDownloadHeaderNode
                reportBlockRef={reportBodyRef}
                fileName={exportFileName}
                useExportData={useExportData}
              />
            ),
            menuComponent: () => (
              <InstantDownloadMenuComponent
                reportBlockRef={reportBodyRef}
                fileName={exportFileName}
                useExportData={useExportData}
              />
            ),
          }),
        }),
    }),
    [
      buildReportBlockNode,
      canExpand,
      expanded,
      showAppliedFilters,
      hasAppliedFilters,
      translate,
      exportFileName,
      useExportData,
    ],
  );

  const visualization = useDataView({
    defaultView: DataViewOption.lineDiagram,
    identifier: id,
    options: [
      {
        view: DataViewOption.lineDiagram,
        options: [DataViewOption.lineDiagram],
        toggles: [
          { id: DataViewToggle.displayTrendLine },
          { id: DataViewToggle.displayDataPoints },
          { id: DataViewToggle.highlightMinMax },
        ],
      },
    ],
  });

  return (
    <ReportBlockProvider id={id} title={title} titleTooltip={titleTooltip} visualization={visualization} nodes={nodes}>
      <SmallLineChartRbBody
        id={id}
        useCurrentData={fetchCurrentHook}
        fetchPreviousHook={fetchPreviousHook}
        reversePercentageColors={reversePercentageColors}
        valueMapper={valueMapper}
        headingValueMapper={headingValueMapper}
        yAxisTickCallback={yAxisTickCallback}
        showTimePeriodFilter={showTimePeriodFilter}
        timePeriod={timePeriod}
        unit={effectiveUnit}
        onTimePeriodChange={setTimePeriod}
        reportBodyRef={reportBodyRef}
        showExpandButton={canExpand}
        canExpand={canExpand}
        isExpandedDesktop={canExpand && expanded}
        showTrendLine={visualization.getToggleState?.(DataViewToggle.displayTrendLine)}
        showDataLabels={visualization.getToggleState?.(DataViewToggle.displayDataPoints)}
        highlightMinMax={visualization.getToggleState?.(DataViewToggle.highlightMinMax)}
      />
    </ReportBlockProvider>
  );
};

const VisitorStatsComparisonToggle = () => {
  const { showPrevious } = useReportBlockCtx();
  const translate = useTranslate();

  return (
    <ReportBlockNodesTooltip
      content={
        <>
          <div className='p-2 mb-1'>
            <Paragraph variant='body' variantSize='large' weight={fontWeights.medium}>
              {translate('button.compareTo')}
            </Paragraph>
          </div>
          <ReportBlockComparisonTooltip />
        </>
      }
    >
      <div>
        <ReportBlockNodeButton
          icon={<CompareIcon color={showPrevious ? '#FFFFFF' : undefined} />}
          className={showPrevious ? 'bg-green-haze!' : undefined}
        />
      </div>
    </ReportBlockNodesTooltip>
  );
};

const SmallLineChartRbBody = ({
  id,
  useCurrentData,
  fetchPreviousHook,
  reversePercentageColors,
  valueMapper,
  headingValueMapper,
  yAxisTickCallback,
  showTimePeriodFilter,
  timePeriod,
  unit,
  onTimePeriodChange,
  reportBodyRef,
  showExpandButton,
  canExpand,
  isExpandedDesktop,
  showTrendLine,
  showDataLabels,
  highlightMinMax,
}: {
  id: string;
  useCurrentData: (unit?: TimePeriod) => SWRResponse<BaseTimeseriesResponse<string>, Error>;
  fetchPreviousHook: (
    activeComparisonOption: DateComparisonPeriod,
    unit?: TimePeriod,
  ) => SWRResponse<BaseTimeseriesResponse<string>, Error>;
  reversePercentageColors?: boolean;
  valueMapper?: (total: number) => any;
  headingValueMapper?: (total: number) => any;
  yAxisTickCallback?: (tickValue: string | number) => string;
  showTimePeriodFilter?: boolean;
  timePeriod: TimePeriod;
  /** The time grouping the data is fetched with (undefined when the card has no time period filter) */
  unit?: TimePeriod;
  onTimePeriodChange: (timePeriod: TimePeriod) => void;
  reportBodyRef: RefObject<HTMLDivElement | null>;
  showExpandButton?: boolean;
  canExpand?: boolean;
  isExpandedDesktop?: boolean;
  showTrendLine?: boolean;
  showDataLabels?: boolean;
  highlightMinMax?: boolean;
}) => {
  const { locale } = useLocale();
  const { showPrevious, activeComparisonOption, nodes } = useReportBlockCtx();

  const getTooltipValueFunc = useCallback(
    (val: any) => (valueMapper ? valueMapper(val) : addNumberSeparator(val, locale)),
    [locale, valueMapper],
  );

  const { chartData, tooltipFn, isLoading, tiles } = useCommonChartViewData<'line'>({
    useCurrentData: () => useCurrentData(unit),
    usePreviousData: () => fetchPreviousHook(activeComparisonOption, unit),
    datasetKeys: [id],
    getTooltipValueFunc,
    reversePercentageColors,
    showPrevious,
  });

  const headingValue = useMemo(() => Number(tiles[0]?.headingValue ?? 0), [tiles]);
  const percentage = useMemo(() => Number(tiles[0]?.percentageChange ?? 0), [tiles]);

  const mapHeadingValue = headingValueMapper ?? valueMapper;

  return (
    <ReportBlockBody className='h-full flex flex-col' ref={reportBodyRef}>
      {nodes?.exportSummary}
      <ReportBlockHeaderProvider>
        <ReportBlockHeader />
        <ReportBlockSubHeader />
      </ReportBlockHeaderProvider>
      <ReportBlockNumericHeading
        percentageChange={percentage}
        value={mapHeadingValue ? mapHeadingValue(headingValue) : addNumberSeparator(headingValue, locale)}
        reversePercentageColors={reversePercentageColors}
        isLoading={isLoading}
      />
      {showTimePeriodFilter && (
        <VisaTimePeriodFilter
          selectedTimePeriod={timePeriod}
          onChange={onTimePeriodChange}
          hideTitle
          shortFormat
          smallButtons
        />
      )}

      <div
        className={classNames('flex-1 relative', isExpandedDesktop ? 'min-h-168' : canExpand ? 'min-h-40' : 'min-h-56')}
      >
        <div className='absolute inset-0'>
          <LineChart
            isLoading={isLoading}
            tooltipFn={tooltipFn}
            id={`${id}-line-diagram-canvas`}
            chartData={chartData}
            className='h-full'
            configOptions={{
              showTrendLine,
              showDataLabels,
              highlightMinMax,
              datalabelsValueFormatter: getTooltipValueFunc,
              yAxisTickCallback,
            }}
          />
        </div>
      </div>
      {showExpandButton && (
        <div className='invisible-on-screenshot'>
          <ExpandButton />
        </div>
      )}
    </ReportBlockBody>
  );
};
